import React, { useState, useEffect } from 'react';
import apiClient from '../config/axios';
import { useNavigate } from 'react-router-dom';
import './ReceiptUploadPage.css';

const ReceiptUploadPage = () => {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // 30 minutes in seconds

  useEffect(() => {
    const storedAccountNumber = localStorage.getItem('accountNumber');
    const storedClientId = localStorage.getItem('clientId');
    
    if (!storedAccountNumber || !storedClientId) {
      navigate('/');
      return;
    }
    
    setClientId(storedClientId);
  }, [navigate]);

  // Timer countdown effect
  useEffect(() => {
    // Don't start timer if already successful
    if (success) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - redirect to home page
          clearInterval(timer);
          localStorage.removeItem('clientId');
          localStorage.removeItem('accountNumber');
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Cleanup interval on unmount or when success
    return () => clearInterval(timer);
  }, [success, navigate]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!file) {
      setError('Please select a receipt file');
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setError('Please accept the terms and conditions to continue');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('receipt', file);
    formData.append('clientId', clientId);

    try {
      await apiClient.post('/api/clients/upload-receipt', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(true);
      setTimeout(() => {
        localStorage.removeItem('clientId');
        localStorage.removeItem('accountNumber');
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error uploading receipt:', err);
      setError(err.response?.data?.error || 'Failed to upload receipt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Account details for all estates
  const accountDetails = [
    {
      accountNumber: '1225218847',
      bankName: 'ZENITH BANK',
      accountName: 'GTLAND LTD'
    },
    {
      accountNumber: '1308305323',
      bankName: 'PROVIDUS BANK',
      accountName: 'GTLAND LTD'
    }
  ];

  return (
    <div className="receipt-upload-page">
      <div className="form-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>Upload Receipt</h1>
          {!success && (
            <div style={{
              padding: '10px 20px',
              backgroundColor: timeRemaining < 300 ? '#dc3545' : '#ffc107', // Red if less than 5 minutes, yellow otherwise
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '18px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              ⏱️ Time Remaining: {formatTime(timeRemaining)}
            </div>
          )}
        </div>
        
        {!success && timeRemaining < 300 && timeRemaining > 0 && (
          <div style={{
            padding: '10px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            <strong>⚠️ Hurry up!</strong> You have less than 5 minutes remaining to complete your payment.
          </div>
        )}
        
        <div className="account-display">
          <h2>Account Numbers</h2>
          <p style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
            Please make payment to any of the following account numbers and upload your receipt below
          </p>
          {accountDetails.map((account, index) => (
            <div key={index} style={{ 
              marginBottom: '20px', 
              padding: '15px', 
              border: '1px solid #ddd', 
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
                {account.accountNumber}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '3px' }}>
                {account.bankName}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {account.accountName}
              </div>
            </div>
          ))}
        </div>

        <div className="form-group" style={{ marginTop: '20px', marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            <span>
              I agree to the{' '}
              <a 
                href="/terms-and-conditions" 
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{ color: '#007bff', textDecoration: 'underline' }}
              >
                Terms and Conditions
              </a>
            </span>
          </label>
        </div>

        {success && (
          <div className="success-message">
            Receipt uploaded successfully! Redirecting to home page...
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="receipt">Upload Receipt *</label>
            <div className="file-upload">
              <input
                type="file"
                id="receipt"
                name="receipt"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                required
              />
            </div>
            {file && (
              <p style={{ marginTop: '10px', color: '#28a745' }}>
                Selected: {file.name}
              </p>
            )}
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading || success || !acceptedTerms}
          >
            {loading ? 'Uploading...' : success ? 'Uploaded!' : 'Submit Receipt'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReceiptUploadPage;
